import {flatten, initial, last} from 'lodash';
import {DateTime} from 'luxon';

import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {padStart} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {CalendarWeekDaysShortEnum} from '../../models/api/calendarCommonTypes';

export function padStartWithZero(n: number) {
  return padStart(n, 2, '0');
}

// This method parses the value from the UI, to a 24h format one
export function to24HHMM(time: TimeAmPm): string | undefined {
  if (time.hh === undefined || time.mm === undefined || time.meridiem === undefined) {
    return undefined;
  }

  const minute = time.mm;
  const hour = to24HH(time.hh, time.meridiem);

  return `${padStartWithZero(hour)}:${padStartWithZero(minute)}`;
}

// if input is 8am, time should be 08:00.
// if input is 7pm, time should be 19:00.
// if input is 12:30am, time should be 00:30.
// if input is 12:30pm, time should be 12:30.
function to24HH(hour: number, meridiem: MeridiemsEnum): number {
  // if value is 12am, hour needs to be 00
  if (hour === 12 && meridiem === MeridiemsEnum.AM) {
    return 0;
  }

  // if value is 12pm, hour should remain 12.
  // if value is 00pm, value should be 12.
  if (hour === 12 || (hour === 0 && meridiem === MeridiemsEnum.PM)) {
    return 12;
  }

  // For the rest, applying offset of (0 or 12h), depending on meridiem.
  const offset = meridiem === MeridiemsEnum.PM ? 12 : 0;
  return hour + offset;
}

// This method parses the value from the server in 24h format, to a value for the UI, HH:MM AM/PM
export function parseHHMM(hhmm: string): TimeAmPm | undefined {
  const parsed = DateTime.fromFormat(hhmm, 'HH:mm');
  if (!parsed.isValid) {
    console.error(`Could not parse ${hhmm} as hh:mm`);
    return undefined;
  }

  const meridiem = parsed.hour >= 12 ? MeridiemsEnum.PM : MeridiemsEnum.AM;

  let hh = parsed.hour >= 12 ? parsed.hour - 12 : parsed.hour;
  if (hh === 0) {
    hh = 12;
  }
  // Midnight is 12PM.
  const mm = parsed.minute;

  return {hh, mm, meridiem};
}

export function safeParseNumber(str: string): number | undefined {
  if (str === '') {
    return undefined;
  }

  const number = Number(str);
  if (Number.isNaN(number)) {
    return undefined;
  }

  return number;
}

export enum MeridiemsEnum {
  AM = 'AM',
  PM = 'PM',
}

export interface TimeAmPm {
  hh?: number;
  mm?: number;

  meridiem?: MeridiemsEnum;
}

export function currentTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const isoDay = {
  [CalendarWeekDaysShortEnum.MONDAY]: 1,
  [CalendarWeekDaysShortEnum.TUESDAY]: 2,
  [CalendarWeekDaysShortEnum.WEDNESDAY]: 3,
  [CalendarWeekDaysShortEnum.THURSDAY]: 4,
  [CalendarWeekDaysShortEnum.FRIDAY]: 5,
  [CalendarWeekDaysShortEnum.SATURDAY]: 6,
  [CalendarWeekDaysShortEnum.SUNDAY]: 7,
} as const as Record<CalendarWeekDaysShortEnum, number>;

// JS-day <-> ISO days
export function isoDayOfWeekToZeroNumbered(isoWeekDay: number): number {
  return isoWeekDay % 7;
}

export function zeroNumberedToIsoDayOfWeek(zeroNumberedDay: number): number {
  return (
    zeroNumberedDay === 0
      ? 7 // Sunday was 0, now is 7.
      : zeroNumberedDay
  ) as number; // Everything else stays the same.
}

export function toIsoDayOfWeek(day: CalendarWeekDaysShortEnum): number {
  return isoDay[day];
}

/**
 * Day sorted in their numerical order
 */
export const daysOfTheWeek = [
  CalendarWeekDaysShortEnum.MONDAY,
  CalendarWeekDaysShortEnum.TUESDAY,
  CalendarWeekDaysShortEnum.WEDNESDAY,
  CalendarWeekDaysShortEnum.THURSDAY,
  CalendarWeekDaysShortEnum.FRIDAY,
  CalendarWeekDaysShortEnum.SATURDAY,
  CalendarWeekDaysShortEnum.SUNDAY,
];

export function fromIsoDayOfWeek(isoDayOfWeek: number): CalendarWeekDaysShortEnum {
  return daysOfTheWeek[(isoDayOfWeek - 1) % daysOfTheWeek.length];
}

export const sundayStartingDaysOfTheWeek = flatten([last(daysOfTheWeek), ...initial(daysOfTheWeek)]).filter(
  isDefined,
);
